<script setup lang="ts">
import { AppTypography } from '@/components';
import type { Qualification } from '@/modules/auth/types/Auth';

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    disabled?: boolean;
    error?: boolean;
    errorMessages?: string[];

    prependItems?: Qualification[];
    appendItems?: Qualification[];

    skeletonCount?: number;
  }>(),
  {
    loading: false,
    disabled: false,
    error: false,
    errorMessages: () => [],
    prependItems: () => [],
    appendItems: () => [],
    skeletonCount: 5,
  }
);

const value = defineModel<Qualification[]>();
const qualifications = ref<Qualification[]>([]);
const { loading: fetchingQualifications, getQualifications } = useGetQualifications();

const isLoading = computed(() => props.loading || fetchingQualifications.value);

const allQualifications = computed(() => [...props.prependItems, ...qualifications.value, ...props.appendItems]);

const hasQualifications = computed(() => allQualifications.value.length > 0);

// Helper function to check if a qualification is selected
const isQualificationSelected = (qualification: Qualification) => {
  return value.value?.some((selected: Qualification) => selected.id === qualification.id) || false;
};

// Helper function to toggle qualification selection
const toggleQualification = (qualification: Qualification) => {
  if (!value.value) {
    value.value = [];
  }
  
  const index = value.value.findIndex((selected: Qualification) => selected.id === qualification.id);
  
  if (index !== -1) {
    // Remove qualification if already selected
    value.value.splice(index, 1);
  } else {
    // Add qualification if not selected
    value.value.push(qualification);
  }
};

const initialize = async (): Promise<void> => {
  qualifications.value = await getQualifications();
};

onMounted(() => {
  nextTick(async () => {
    await initialize();
  });
});
</script>

<template>
  <div class="select-qualifications">
    <template v-if="!isLoading">
      <template v-if="hasQualifications">
        <v-card
          v-for="qualification in allQualifications"
          :key="qualification.id"
          class="select-qualifications__item"
          flat
        >
          <label :for="`checkbox-${qualification.id}`">
            <v-card-text>
              <div class="d-flex flex-row justify-space-between align-center">
                <div class="select-qualifications__item__content flex-grow-1">
                  <AppTypography type="headline-1" class="select-qualifications__item__name">
                    {{ qualification.name }}
                  </AppTypography>
                  <AppTypography
                    v-if="qualification.description"
                    type="subheadline-2"
                    class="select-qualifications__item__description mt-2"
                  >
                    {{ qualification.description }}
                  </AppTypography>
                </div>
                <v-checkbox
                  :id="`checkbox-${qualification.id}`"
                  :model-value="isQualificationSelected(qualification)"
                  :disabled="isLoading || disabled"
                  density="compact"
                  color="primary"
                  class="select-qualifications__item__btn"
                  hide-details
                  @update:model-value="toggleQualification(qualification)"
                />
              </div>
            </v-card-text>
          </label>
        </v-card>
      </template>
      <template v-else>
        <v-card class="select-qualifications__placeholder" flat>
          <v-card-text class="d-flex justify-center align-center">
            <AppTypography type="subheadline-2">No qualifications found</AppTypography>
          </v-card-text>
        </v-card>
      </template>
    </template>

    <template v-else>
      <v-card v-for="n in skeletonCount" :key="n" class="select-qualifications__loader" flat>
        <v-card-text>
          <v-skeleton-loader type="list-item-two-line" />
        </v-card-text>
      </v-card>
    </template>

    <!-- Error messages display -->
    <div v-if="error && errorMessages.length > 0" class="select-qualifications__errors mt-3">
        <div v-for="message in errorMessages" :key="message" class="error-message">
          {{ message }}
        </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.select-qualifications {
  .v-card {
    border-radius: 16px;

    label {
      cursor: pointer !important;
    }
  }

  &__item,
  &__loader {
    &:not(:last-child) {
      margin-bottom: 20px;
    }
  }

  &__item {
    &__content {
      max-width: 90%;
    }
  }

  &__errors {
    .error-message {
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
      color: red;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>
